import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Fragment, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useUsersOverviewQuery } from '../shared/queries';
import { UserOverview, UserOverviewPayment } from '@features/profile/api';

const DATE_FORMAT = 'DD.MM.YYYY';
const DATETIME_FORMAT = 'DD.MM.YYYY HH:mm';

const formatDate = (date: string | null) => {
  if (!date) {
    return '-';
  }

  return dayjs(date).format(DATE_FORMAT);
};

const getSubscriptionStatusChipColor = (status: UserOverview['subscriptionStatus']) => {
  switch (status) {
    case 'ACTIVE':
      return 'success' as const;
    case 'PENDING':
      return 'warning' as const;
    case 'CANCELLED':
    case 'EXPIRED':
      return 'error' as const;
    default:
      return 'default' as const;
  }
};

const getPaymentStatusChipColor = (status: UserOverview['lastPaymentStatus']) => {
  switch (status) {
    case 'PAID':
      return 'success' as const;
    case 'PENDING':
    case 'AUTHORIZED':
      return 'warning' as const;
    case 'CANCELLED':
      return 'error' as const;
    default:
      return 'default' as const;
  }
};

const paymentRowColor = (status: UserOverviewPayment['status']) => {
  switch (status) {
    case 'PAID':
      return 'success' as const;
    case 'PENDING':
    case 'AUTHORIZED':
      return 'warning' as const;
    case 'CANCELLED':
      return 'error' as const;
    default:
      return 'default' as const;
  }
};

const UsersOverview = () => {
  const { data, isLoading, isError } = useUsersOverviewQuery();
  const [openedRows, setOpenedRows] = useState<Record<string, boolean>>({});

  const sortedData = useMemo(() => {
    const statusOrder: Record<string, number> = { ACTIVE: 0, PENDING: 1, CANCELLED: 2, EXPIRED: 3 };
    return [...(data ?? [])].sort((left, right) => {
      const statusDiff =
        (statusOrder[left.subscriptionStatus ?? ''] ?? 99) -
        (statusOrder[right.subscriptionStatus ?? ''] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      return left.name.localeCompare(right.name, 'cs');
    });
  }, [data]);

  const toggleRow = (userId: string) => {
    setOpenedRows(previous => ({
      ...previous,
      [userId]: !previous[userId],
    }));
  };

  if (isLoading) {
    return <Typography>Načítám přehled uživatelů…</Typography>;
  }

  if (isError) {
    return <Alert severity="error">Nepovedlo se načíst přehled uživatelů.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Přehled uživatelů a plateb</Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={56} />
              <TableCell>Jméno</TableCell>
              <TableCell>Stav předplatného</TableCell>
              <TableCell>Stav poslední platby</TableCell>
              <TableCell>Datumy</TableCell>
              <TableCell>Poslední log</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedData.map(user => {
              const isOpen = Boolean(openedRows[user.userId]);

              return (
                <Fragment key={user.userId}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        aria-label="Rozbalit detail plateb"
                        size="small"
                        onClick={() => toggleRow(user.userId)}
                      >
                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography fontWeight={600}>{user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.subscriptionStatus ?? '-'}
                        color={getSubscriptionStatusChipColor(user.subscriptionStatus)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.lastPaymentStatus ?? '-'}
                        color={getPaymentStatusChipColor(user.lastPaymentStatus)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'info.main',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 110 }}
                          >
                            Začátek v aplikaci
                          </Typography>
                          <Typography variant="caption">{formatDate(user.appStartDate)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'warning.main',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 110 }}
                          >
                            Další platba
                          </Typography>
                          <Typography variant="caption">
                            {formatDate(user.nextPaymentDate)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'error.main',
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 110 }}
                          >
                            Konec předplatného
                          </Typography>
                          <Typography variant="caption">
                            {formatDate(user.subscriptionEndDate)}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {user.lastLog ? (
                        <Stack>
                          <Typography variant="caption">{user.lastLog.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(user.lastLog.createdAt).format(DATETIME_FORMAT)}
                          </Typography>
                        </Stack>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={6}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 2, py: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Detail plateb ({user.payments.length})
                          </Typography>

                          {user.payments.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Uživatel zatím nemá žádnou platbu.
                            </Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Datum</TableCell>
                                  <TableCell>Stav</TableCell>
                                  <TableCell>Částka</TableCell>
                                  <TableCell>Ref ID</TableCell>
                                  <TableCell>Transaction ID</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {user.payments.map(payment => (
                                  <TableRow key={payment.id}>
                                    <TableCell>
                                      {dayjs(payment.createdAt).format(DATETIME_FORMAT)}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={payment.status}
                                        color={paymentRowColor(payment.status)}
                                      />
                                    </TableCell>
                                    <TableCell>{`${payment.amount} ${payment.currency}`}</TableCell>
                                    <TableCell>{payment.refId}</TableCell>
                                    <TableCell>{payment.transactionId}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default UsersOverview;
